import { FormEvent, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  Loader2,
  Send,
} from 'lucide-react';

// ── Country dial-code data ────────────────────────────────────────────────────

interface CountryDialCode {
  flag: string;
  name: string;
  code: string;
  dial: string;
}

const COUNTRY_CODES: CountryDialCode[] = [
  { flag: '🇮🇳', name: 'India',          code: 'IN', dial: '+91'  },
  { flag: '🇺🇸', name: 'United States',   code: 'US', dial: '+1'   },
  { flag: '🇬🇧', name: 'United Kingdom',  code: 'GB', dial: '+44'  },
  { flag: '🇦🇪', name: 'UAE',             code: 'AE', dial: '+971' },
  { flag: '🇸🇬', name: 'Singapore',       code: 'SG', dial: '+65'  },
  { flag: '🇦🇺', name: 'Australia',       code: 'AU', dial: '+61'  },
  { flag: '🇨🇦', name: 'Canada',          code: 'CA', dial: '+1'   },
  { flag: '🇩🇪', name: 'Germany',         code: 'DE', dial: '+49'  },
  { flag: '🇫🇷', name: 'France',          code: 'FR', dial: '+33'  },
  { flag: '🇳🇱', name: 'Netherlands',     code: 'NL', dial: '+31'  },
  { flag: '🇸🇦', name: 'Saudi Arabia',    code: 'SA', dial: '+966' },
  { flag: '🇶🇦', name: 'Qatar',           code: 'QA', dial: '+974' },
  { flag: '🇧🇭', name: 'Bahrain',         code: 'BH', dial: '+973' },
  { flag: '🇰🇼', name: 'Kuwait',          code: 'KW', dial: '+965' },
  { flag: '🇴🇲', name: 'Oman',            code: 'OM', dial: '+968' },
  { flag: '🇿🇦', name: 'South Africa',    code: 'ZA', dial: '+27'  },
  { flag: '🇮🇪', name: 'Ireland',         code: 'IE', dial: '+353' },
  { flag: '🇳🇿', name: 'New Zealand',     code: 'NZ', dial: '+64'  },
  { flag: '🇯🇵', name: 'Japan',           code: 'JP', dial: '+81'  },
  { flag: '🇰🇷', name: 'South Korea',     code: 'KR', dial: '+82'  },
  { flag: '🇨🇳', name: 'China',           code: 'CN', dial: '+86'  },
  { flag: '🇧🇷', name: 'Brazil',          code: 'BR', dial: '+55'  },
  { flag: '🇲🇽', name: 'Mexico',          code: 'MX', dial: '+52'  },
  { flag: '🇮🇹', name: 'Italy',           code: 'IT', dial: '+39'  },
  { flag: '🇪🇸', name: 'Spain',           code: 'ES', dial: '+34'  },
  { flag: '🇸🇪', name: 'Sweden',          code: 'SE', dial: '+46'  },
  { flag: '🇳🇴', name: 'Norway',          code: 'NO', dial: '+47'  },
  { flag: '🇩🇰', name: 'Denmark',         code: 'DK', dial: '+45'  },
  { flag: '🇫🇮', name: 'Finland',         code: 'FI', dial: '+358' },
  { flag: '🇨🇭', name: 'Switzerland',     code: 'CH', dial: '+41'  },
  { flag: '🇵🇱', name: 'Poland',          code: 'PL', dial: '+48'  },
  { flag: '🇷🇺', name: 'Russia',          code: 'RU', dial: '+7'   },
  { flag: '🇹🇷', name: 'Turkey',          code: 'TR', dial: '+90'  },
  { flag: '🇵🇰', name: 'Pakistan',        code: 'PK', dial: '+92'  },
  { flag: '🇧🇩', name: 'Bangladesh',      code: 'BD', dial: '+880' },
  { flag: '🇱🇰', name: 'Sri Lanka',       code: 'LK', dial: '+94'  },
  { flag: '🇳🇵', name: 'Nepal',           code: 'NP', dial: '+977' },
  { flag: '🇲🇾', name: 'Malaysia',        code: 'MY', dial: '+60'  },
  { flag: '🇮🇩', name: 'Indonesia',       code: 'ID', dial: '+62'  },
  { flag: '🇵🇭', name: 'Philippines',     code: 'PH', dial: '+63'  },
  { flag: '🇹🇭', name: 'Thailand',        code: 'TH', dial: '+66'  },
  { flag: '🇻🇳', name: 'Vietnam',         code: 'VN', dial: '+84'  },
  { flag: '🇪🇬', name: 'Egypt',           code: 'EG', dial: '+20'  },
  { flag: '🇳🇬', name: 'Nigeria',         code: 'NG', dial: '+234' },
  { flag: '🇰🇪', name: 'Kenya',           code: 'KE', dial: '+254' },
];

// ── Shared input class tokens (mirror B2C, theme-compatible) ─────────────────

/**
 * Base field — matches B2C `bg-[#1e293b]/50 border text-[#f1f5f9]` on dark,
 * plus a light-mode equivalent so it works in both themes.
 */
const fieldBase =
  'w-full min-h-[38px] px-3 py-1.5 rounded-lg text-sm transition-colors ' +
  'bg-gray-50 dark:bg-[#1e293b]/50 ' +
  'text-gray-900 dark:text-[#f1f5f9] ' +
  'placeholder-gray-400 dark:placeholder-[#64748b] ' +
  'focus:outline-none focus:ring-1 ' +
  'focus:border-[#8b80f9]/60 focus:ring-[#8b80f9]/30';

const fieldNormal = `${fieldBase} border border-gray-300 dark:border-gray-600`;
const fieldError  = `${fieldBase} border border-red-500`;

const labelClass = 'block text-[0.8125rem] font-semibold text-gray-700 dark:text-[#cbd5e1] mb-1';

// ── Types ────────────────────────────────────────────────────────────────────

interface FormData {
  firstName:   string;
  lastName:    string;
  email:       string;
  company:     string;
  phoneCode:   string;
  phoneNumber: string;
  companySize: string;
  message:     string;
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error' | 'validation-error';

type ValidationErrors = Partial<Record<keyof FormData, string>>;

const EMPTY_FORM: FormData = {
  firstName:   '',
  lastName:    '',
  email:       '',
  company:     '',
  phoneCode:   '+91',
  phoneNumber: '',
  companySize: '',
  message:     '',
};

// ── Component ────────────────────────────────────────────────────────────────

interface B2BDemoLeadFormProps {
  className?: string;
}

/**
 * Demo / contact lead-capture form for B2B.
 *
 * Mirrors B2C ContactUsPage form structure:
 *   • Inline success / error banners above the fields (not full-page replacement)
 *   • First + Last name in a 2-column grid
 *   • B2C-style input tokens adapted for light + dark themes
 *   • Submits to ggj_b2b_send-contact-email edge function (fallback: mailto)
 */
export function B2BDemoLeadForm({ className = '' }: B2BDemoLeadFormProps) {
  const [searchParams] = useSearchParams();

  const [formData, setFormData]         = useState<FormData>(EMPTY_FORM);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [dialOpen, setDialOpen] = useState(false);
  const dialRef = useRef<HTMLDivElement>(null);

  // Pre-fill email from ?email= query param
  useEffect(() => {
    const fromUrl = searchParams.get('email');
    if (fromUrl) {
      setFormData((prev) => ({ ...prev, email: prev.email || decodeURIComponent(fromUrl.trim()) }));
    }
  }, [searchParams]);

  // Close country-code dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dialRef.current && !dialRef.current.contains(e.target as Node)) {
        setDialOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setSubmitStatus('idle');
    if (validationErrors[name as keyof FormData]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name as keyof FormData];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const errors: ValidationErrors = {};

    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.email.trim()) {
      errors.email = 'Work email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.company.trim()) errors.company = 'Company name is required';
    if (!formData.message.trim()) {
      errors.message = "Please tell us what you'd like to solve";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      setSubmitStatus('validation-error');
      return;
    }

    setIsSubmitting(true);

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

    const fullPhone = formData.phoneNumber.trim()
      ? `${formData.phoneCode} ${formData.phoneNumber.trim()}`
      : undefined;

    if (supabaseUrl && anonKey) {
      try {
        const res = await fetch(
          `${supabaseUrl}/functions/v1/ggj_b2b_send-contact-email`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${anonKey}`,
            },
            body: JSON.stringify({
              name:        `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim(),
              email:       formData.email.trim(),
              company:     formData.company.trim(),
              phone:       fullPhone,
              companySize: formData.companySize || undefined,
              message:     formData.message.trim() || undefined,
            }),
          },
        );

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          console.error('[B2BDemoLeadForm] edge function error:', err);
          setSubmitStatus('error');
          setIsSubmitting(false);
          return;
        }

        setSubmitStatus('success');
        // Mirror B2C: reset the form 3 s after a successful send
        setTimeout(() => {
          setFormData(EMPTY_FORM);
          setSubmitStatus('idle');
        }, 3000);
        setIsSubmitting(false);
        return;
      } catch (err) {
        console.error('[B2BDemoLeadForm] fetch error:', err);
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }
    }

    // Fallback: mailto when no edge function is available (offline dev)
    const subject = encodeURIComponent(
      `GoGrabJob Business — demo request (${formData.company})`,
    );
    const body = encodeURIComponent(
      [
        `Name: ${formData.firstName} ${formData.lastName}`,
        `Work email: ${formData.email}`,
        `Company: ${formData.company}`,
        `Phone: ${fullPhone ?? '—'}`,
        `Company size: ${formData.companySize || '—'}`,
        '',
        formData.message || '(no additional message)',
      ].join('\n'),
    );
    window.location.href = `mailto:support@gograbjob.com?subject=${subject}&body=${body}`;
    setSubmitStatus('success');
    setIsSubmitting(false);
  };

  const selectedCountry = COUNTRY_CODES.find((c) => c.dial === formData.phoneCode);

  return (
    <form onSubmit={handleSubmit} className={`space-y-2 ${className}`} noValidate>

      {/* ── Success banner (mirrors B2C CheckCircle banner) ───────── */}
      {submitStatus === 'success' && (
        <motion.div
          className="w-full p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-600 dark:text-green-400 text-sm"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <p>
              Thanks! Your demo request was sent. We'll reach out to{' '}
              <span className="font-semibold">{formData.email || 'you'}</span> within 24 hours.
            </p>
          </div>
        </motion.div>
      )}

      {/* ── Error banners (mirrors B2C AlertCircle banner) ────────── */}
      {(submitStatus === 'error' || submitStatus === 'validation-error') && (
        <motion.div
          className="w-full p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-600 dark:text-red-400 text-sm"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <p>
              {submitStatus === 'validation-error'
                ? 'Please fix the errors below and try again.'
                : 'Something went wrong. Please try again or email us at support@gograbjob.com'}
            </p>
          </div>
        </motion.div>
      )}

      {/* ── First name + Last name (2-col, mirrors B2C) ───────────── */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="demo-firstName" className={labelClass}>
            First name <span className="text-red-500">*</span>
          </label>
          <input
            id="demo-firstName"
            name="firstName"
            type="text"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="Jane"
            autoComplete="given-name"
            className={validationErrors.firstName ? fieldError : fieldNormal}
          />
          {validationErrors.firstName && (
            <p className="mt-1 text-sm text-red-500 dark:text-red-400">{validationErrors.firstName}</p>
          )}
        </div>

        <div>
          <label htmlFor="demo-lastName" className={labelClass}>
            Last name
          </label>
          <input
            id="demo-lastName"
            name="lastName"
            type="text"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Smith"
            autoComplete="family-name"
            className={fieldNormal}
          />
        </div>
      </div>

      {/* ── Work email ────────────────────────────────────────────── */}
      <div>
        <label htmlFor="demo-email" className={labelClass}>
          Work email <span className="text-red-500">*</span>
        </label>
        <input
          id="demo-email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="jane@company.com"
          autoComplete="email"
          className={validationErrors.email ? fieldError : fieldNormal}
        />
        {validationErrors.email && (
          <p className="mt-1 text-sm text-red-500 dark:text-red-400">{validationErrors.email}</p>
        )}
      </div>

      {/* ── Company ───────────────────────────────────────────────── */}
      <div>
        <label htmlFor="demo-company" className={labelClass}>
          Company <span className="text-red-500">*</span>
        </label>
        <input
          id="demo-company"
          name="company"
          type="text"
          value={formData.company}
          onChange={handleChange}
          placeholder="Acme Recruiting"
          autoComplete="organization"
          className={validationErrors.company ? fieldError : fieldNormal}
        />
        {validationErrors.company && (
          <p className="mt-1 text-sm text-red-500 dark:text-red-400">{validationErrors.company}</p>
        )}
      </div>

      {/* ── Phone — country-code picker + number ──────────────────── */}
      <div>
        <label className={labelClass}>Phone</label>
        <div className="flex gap-2">

          {/* Country-code button + dropdown */}
          <div ref={dialRef} className="relative flex-shrink-0">
            <button
              type="button"
              onClick={() => setDialOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={dialOpen}
              className={
                'flex items-center gap-1.5 min-h-[38px] px-3 rounded-lg border text-sm transition-colors ' +
                'bg-gray-50 dark:bg-[#1e293b]/50 ' +
                'text-gray-800 dark:text-[#f1f5f9] ' +
                'border-gray-300 dark:border-gray-600 ' +
                'hover:border-[#8b80f9]/60 focus:outline-none focus:ring-1 ' +
                'focus:border-[#8b80f9]/60 focus:ring-[#8b80f9]/30'
              }
            >
              <span className="text-lg leading-none">{selectedCountry?.flag ?? '🌐'}</span>
              <span className="tabular-nums text-sm text-gray-600 dark:text-[#94a3b8] min-w-[38px]">
                {formData.phoneCode}
              </span>
              <ChevronDown
                className={`w-3.5 h-3.5 text-gray-400 transition-transform ${dialOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {dialOpen && (
              <div
                role="listbox"
                className={
                  'absolute left-0 top-full mt-1 z-50 w-64 max-h-60 overflow-y-auto rounded-lg shadow-xl py-1 ' +
                  'bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-gray-600'
                }
              >
                {COUNTRY_CODES.map((c) => (
                  <button
                    key={`${c.code}-${c.dial}`}
                    type="button"
                    role="option"
                    aria-selected={formData.phoneCode === c.dial}
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, phoneCode: c.dial }));
                      setDialOpen(false);
                    }}
                    className={
                      'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ' +
                      (formData.phoneCode === c.dial
                        ? 'bg-[#8b80f9]/10 text-[#6d5fd4] dark:text-[#a09af9] font-medium'
                        : 'text-gray-700 dark:text-[#cbd5e1] hover:bg-gray-50 dark:hover:bg-[#1e293b]/80')
                    }
                  >
                    <span className="text-base w-5 text-center flex-shrink-0">{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="tabular-nums text-xs text-gray-400 dark:text-[#64748b]">{c.dial}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Number input */}
          <input
            id="demo-phone"
            name="phoneNumber"
            type="tel"
            value={formData.phoneNumber}
            onChange={handleChange}
            placeholder="98765 43210"
            autoComplete="tel-national"
            className={`flex-1 ${fieldNormal}`}
          />
        </div>
      </div>

      {/* ── Company size ──────────────────────────────────────────── */}
      <div>
        <label htmlFor="demo-size" className={labelClass}>
          Company size
        </label>
        <select
          id="demo-size"
          name="companySize"
          value={formData.companySize}
          onChange={handleChange}
          className={`${fieldNormal} appearance-none`}
        >
          <option value="">Select…</option>
          <option value="1–10">1 – 10</option>
          <option value="11–50">11 – 50</option>
          <option value="51–200">51 – 200</option>
          <option value="201–1000">201 – 1,000</option>
          <option value="1000+">1,000+</option>
        </select>
      </div>

      {/* ── Message ───────────────────────────────────────────────── */}
      <div>
        <label htmlFor="demo-message" className={labelClass}>
          What are you hoping to solve? <span className="text-red-500">*</span>
        </label>
        <textarea
          id="demo-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={2}
          placeholder="e.g. We need to reduce time-to-hire for tech roles and automate candidate screening…"
          className={`${validationErrors.message ? fieldError : fieldNormal} resize-none`}
        />
        {validationErrors.message && (
          <p className="mt-1 text-sm text-red-500 dark:text-red-400">{validationErrors.message}</p>
        )}
      </div>

      {/* ── Submit (mirrors B2C indigo button) ────────────────────── */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full flex items-center justify-center py-2.5 px-6 bg-[#8b80f9] hover:bg-[#7c72f0] text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Sending…
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Book my demo
          </>
        )}
      </button>
    </form>
  );
}
